import React, { useRef, useState } from 'react';
import { Autocomplete } from '@react-google-maps/api';

/**
 * Per-member trusted-locations editor.
 *
 * Used inside the expanded MemberRow of FamilyProfilePage. Writes to the parent
 * via `onChange(nextLocations)`. `onToggleStrict(nextBool)` flips the
 * "only allow rides to these locations" switch.
 *
 * Data model (matches backend `TrustedLocation`):
 *   { id?, name, address?, latitude, longitude }
 */
const TrustedLocationsManager = ({
  locations = [],
  strict = false,
  onChange,
  onToggleStrict,
}) => {
  const [draftName, setDraftName] = useState('');
  const [draftPlace, setDraftPlace] = useState(null); // { address, latitude, longitude }
  const autoRef = useRef(null);

  const onLoad = (ac) => { autoRef.current = ac; };

  const onPlaceChanged = () => {
    const place = autoRef.current?.getPlace();
    if (!place?.geometry?.location) return;
    setDraftPlace({
      address: place.formatted_address || place.name || '',
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
    });
  };

  const handleAdd = () => {
    if (!draftName.trim()) { alert('Give this place a short name (e.g. Home, School)'); return; }
    if (!draftPlace) { alert('Pick a location from the suggestions'); return; }
    const next = [
      ...(locations || []),
      { name: draftName.trim(), address: draftPlace.address, latitude: draftPlace.latitude, longitude: draftPlace.longitude },
    ];
    onChange(next);
    setDraftName('');
    setDraftPlace(null);
    // Clear autocomplete input
    const inp = document.getElementById('trusted-loc-ac-input');
    if (inp) inp.value = '';
  };

  const handleRemove = (i) => {
    const next = (locations || []).filter((_, idx) => idx !== i);
    onChange(next);
  };

  const googleReady = typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places;

  return (
    <div className="mt-2" data-testid="trusted-locations-manager">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          Trusted locations
        </p>
        <label className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            checked={!!strict}
            onChange={(e) => onToggleStrict(e.target.checked)}
            data-testid="trusted-locations-strict-toggle"
          />
          <span>Restrict rides to these places only</span>
        </label>
      </div>

      {(locations || []).length === 0 ? (
        <p className="text-[11px] text-gray-500 italic mb-2">No trusted locations added yet.</p>
      ) : (
        <ul className="space-y-1.5 mb-2">
          {(locations || []).map((loc, i) => (
            <li
              key={loc.id || `${loc.latitude}-${loc.longitude}-${i}`}
              className="flex items-start justify-between gap-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 text-xs"
              data-testid="trusted-location-row"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 dark:text-white truncate">{loc.name}</p>
                <p className="text-[10px] text-gray-500 truncate">{loc.address || `${loc.latitude?.toFixed?.(4)}, ${loc.longitude?.toFixed?.(4)}`}</p>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="text-red-600 hover:text-red-700 text-[11px] px-1.5"
                data-testid="trusted-location-remove"
                aria-label={`Remove ${loc.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="bg-gray-50 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-2 space-y-1.5">
        <input
          type="text"
          placeholder="Label (Home, School, Nana's house…)"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          data-testid="trusted-location-name-input"
          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs dark:bg-gray-900 dark:text-white"
        />
        {googleReady ? (
          <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
            <input
              id="trusted-loc-ac-input"
              type="text"
              placeholder="Search address…"
              data-testid="trusted-location-address-input"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs dark:bg-gray-900 dark:text-white"
            />
          </Autocomplete>
        ) : (
          <p className="text-[10px] text-amber-600">Map search not ready — reload the page.</p>
        )}
        {draftPlace && (
          <p className="text-[10px] text-green-700 dark:text-green-400 truncate" data-testid="trusted-location-preview">
            ✓ {draftPlace.address}
          </p>
        )}
        <button
          type="button"
          onClick={handleAdd}
          data-testid="trusted-location-add-btn"
          className="w-full bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 text-white text-xs font-medium py-1.5 rounded"
          disabled={!draftName.trim() || !draftPlace}
        >
          Add location
        </button>
      </div>
    </div>
  );
};

export default TrustedLocationsManager;
